import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const GREEN = '#15803d';
const GREEN_DARK = '#166534';
const GREEN_LIGHT = '#f0fdf4';
const GREEN_BORDER = '#dcfce7';
const GRAY_50 = '#F9FAFB';
const GRAY_200 = '#E5E7EB';
const GRAY_400 = '#9CA3AF';
const GRAY_500 = '#6B7280';
const GRAY_700 = '#374151';
const GRAY_900 = '#111827';

const STATUS_LABELS: Record<string, string> = {
  RASCUNHO: 'Rascunho', ENVIADO: 'Enviado', APROVADO: 'Aprovado',
  REJEITADO: 'Rejeitado', CANCELADO: 'Cancelado',
};

const INSTALACAO_LABELS: Record<string, string> = {
  PAREDE: 'Parede', TETO: 'Teto', ENTRE_VAOS: 'Entre vaos',
};

const ACIONAMENTO_LABELS: Record<string, string> = {
  MANUAL_CORDA: 'Manual (Corda)',
  MANUAL_CORRENTE: 'Manual (Corrente)',
  MOTORIZADO: 'Motorizado',
  AUTOMATICO: 'Automatico',
};

const LADO_COMANDO_LABELS: Record<string, string> = {
  DIREITA: 'Direita',
  ESQUERDA: 'Esquerda',
};

const s = StyleSheet.create({
  page: { fontFamily: 'Helvetica', fontSize: 9, color: GRAY_900, paddingBottom: 50 },

  // Header
  header: { backgroundColor: GREEN, paddingHorizontal: 40, paddingTop: 26, paddingBottom: 20 },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' },
  headerCompany: { color: '#FFFFFF', fontSize: 20, fontFamily: 'Helvetica-Bold' },
  headerTagline: { color: '#bbf7d0', fontSize: 8, marginTop: 3 },
  headerRight: { alignItems: 'flex-end' },
  headerNumero: { color: '#FFFFFF', fontSize: 14, fontFamily: 'Helvetica-Bold' },
  headerData: { color: '#bbf7d0', fontSize: 8, marginTop: 2 },
  statusPill: {
    marginTop: 6, backgroundColor: 'rgba(255,255,255,0.22)',
    borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3,
  },
  statusText: { color: '#FFFFFF', fontSize: 8, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase' },

  // Body
  body: { paddingHorizontal: 40 },

  // Two-column info
  infoSection: { flexDirection: 'row', marginTop: 20, marginBottom: 4 },
  infoColL: { flex: 1, paddingRight: 18 },
  infoColR: { flex: 1, paddingLeft: 18, borderLeft: `1pt solid ${GRAY_200}` },
  sectionTitle: {
    fontSize: 7, fontFamily: 'Helvetica-Bold', color: GREEN,
    textTransform: 'uppercase', letterSpacing: 1.2,
    borderBottom: `1pt solid ${GREEN_BORDER}`, paddingBottom: 3, marginBottom: 8,
  },
  infoRow: { flexDirection: 'row', marginBottom: 4 },
  infoLabel: { color: GRAY_500, fontSize: 8, width: 64, flexShrink: 0 },
  infoValue: { color: GRAY_900, fontSize: 8, flex: 1 },
  infoValueBold: { color: GRAY_900, fontSize: 8, fontFamily: 'Helvetica-Bold', flex: 1 },
  divider: { borderBottom: `0.5pt solid ${GRAY_200}`, marginVertical: 14 },

  // Item card
  itemCard: { marginBottom: 10, border: `1pt solid ${GRAY_200}`, borderRadius: 4 },
  itemHeader: {
    backgroundColor: GREEN_LIGHT, borderTopLeftRadius: 3, borderTopRightRadius: 3,
    borderBottom: `1pt solid ${GREEN_BORDER}`,
    paddingHorizontal: 10, paddingVertical: 7,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start',
  },
  itemHeaderLeft: { flexDirection: 'row', alignItems: 'flex-start', flex: 1 },
  itemBadge: {
    backgroundColor: GREEN, color: '#FFFFFF', fontSize: 8,
    fontFamily: 'Helvetica-Bold', width: 18, height: 18, borderRadius: 9,
    textAlign: 'center', paddingTop: 3, marginRight: 8, flexShrink: 0,
  },
  itemAmbiente: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: GREEN_DARK },
  itemDescricao: { fontSize: 8, color: GRAY_500, marginTop: 1 },
  itemSubtotalBlock: { alignItems: 'flex-end' },
  itemSubtotalLabel: { fontSize: 7, color: GRAY_500 },
  itemSubtotalValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: GREEN },

  itemBody: { padding: 10 },

  // Measurements
  medidasRow: { flexDirection: 'row', marginBottom: 8 },
  medidaBox: {
    flex: 1, backgroundColor: GRAY_50, borderRadius: 4, padding: 5,
    marginRight: 5, alignItems: 'center',
  },
  medidaBoxLast: { flex: 1, backgroundColor: GRAY_50, borderRadius: 4, padding: 5, alignItems: 'center' },
  medidaLabel: { fontSize: 7, color: GRAY_400, marginBottom: 1 },
  medidaValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: GRAY_900, textAlign: 'center' },

  // Product block
  produtoBox: {
    backgroundColor: GRAY_50, borderRadius: 4, padding: 8,
    marginBottom: 8, borderLeft: `3pt solid ${GREEN}`,
  },
  produtoNome: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: GRAY_900, marginBottom: 5 },
  produtoGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  produtoItem: { flexDirection: 'row', width: '50%', marginBottom: 3 },

  // Prices
  precosRow: {
    flexDirection: 'row', justifyContent: 'flex-end',
    paddingTop: 6, marginBottom: 6, borderTop: `0.5pt solid ${GRAY_200}`,
  },
  precoBox: { alignItems: 'flex-end', marginLeft: 18 },
  precoLabel: { fontSize: 7, color: GRAY_400 },
  precoValue: { fontSize: 8, color: GRAY_700 },

  // Photos
  fotosSection: { borderTop: `0.5pt solid ${GRAY_200}`, paddingTop: 8, marginTop: 2 },
  fotosSectionLabel: {
    fontSize: 7, color: GRAY_500, fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
  },
  fotosRow: { flexDirection: 'row', flexWrap: 'wrap' },
  fotoImg: { width: 106, height: 78, borderRadius: 3, marginRight: 6, marginBottom: 6 },

  obsItem: { marginTop: 5, paddingTop: 5, borderTop: `0.5pt dashed ${GRAY_200}` },
  obsText: { fontSize: 8, color: GRAY_500, fontFamily: 'Helvetica-Oblique' },

  // Financial summary
  summarySection: { marginTop: 16 },
  summaryCard: {
    backgroundColor: GREEN_LIGHT, borderRadius: 4, padding: 14, border: `1pt solid ${GREEN_BORDER}`,
  },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 5 },
  summaryLabel: { fontSize: 9, color: GRAY_700 },
  summaryValue: { fontSize: 9, color: GRAY_700 },
  summaryDiscount: { fontSize: 9, color: '#DC2626' },
  summaryLine: { borderBottom: `1pt solid #86EFAC`, marginVertical: 8 },
  summaryTotalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryTotalLabel: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: GREEN },
  summaryTotalValue: { fontSize: 15, fontFamily: 'Helvetica-Bold', color: GREEN },

  // Notes
  notesSection: { marginTop: 16 },
  notesBox: { backgroundColor: '#FFFBEB', borderRadius: 4, padding: 10, border: `1pt solid #FDE68A` },
  notesText: { fontSize: 9, color: GRAY_700, lineHeight: 1.5 },

  // Closing
  closingSection: {
    marginTop: 24, alignItems: 'center',
    borderTop: `0.5pt solid ${GRAY_200}`, paddingTop: 12,
  },
  closingText: { fontSize: 8, color: GRAY_400, textAlign: 'center', lineHeight: 1.6 },

  // Footer (fixed on every page)
  footer: {
    position: 'absolute', bottom: 18, left: 40, right: 40,
    flexDirection: 'row', justifyContent: 'space-between',
    borderTop: `0.5pt solid ${GRAY_200}`, paddingTop: 6,
  },
  footerText: { fontSize: 7, color: GRAY_400 },
});

interface Props {
  orcamento: any;
  apiBase: string;
  mode?: 'interno' | 'cliente';
}

export function OrcamentoPDF({ orcamento, apiBase, mode = 'interno' }: Props) {
  const isCliente = mode === 'cliente';
  const fmt = (v: number) =>
    (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const area = (l: number, a: number) => (l / 100) * (a / 100);
  const validadeStr = orcamento.dataValidade
    ? new Date(orcamento.dataValidade).toLocaleDateString('pt-BR')
    : null;

  return (
    <Document
      title={`Orcamento ${orcamento.numero}`}
      author="Palmeira Persianas"
      subject="Orcamento de Persianas"
    >
      <Page size="A4" style={s.page}>

        {/* ── HEADER ── */}
        <View style={s.header}>
          <View style={s.headerRow}>
            <View>
              <Text style={s.headerCompany}>Palmeira Persianas</Text>
              <Text style={s.headerTagline}>Persianas e Cortinas sob medida</Text>
            </View>
            <View style={s.headerRight}>
              <Text style={s.headerNumero}>{orcamento.numero}</Text>
              <Text style={s.headerData}>
                Emissao: {new Date(orcamento.criadoEm).toLocaleDateString('pt-BR')}
              </Text>
              <View style={s.statusPill}>
                <Text style={s.statusText}>{STATUS_LABELS[orcamento.status] ?? orcamento.status}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={s.body}>

          {/* ── CLIENTE + ORCAMENTO ── */}
          <View style={s.infoSection}>
            <View style={s.infoColL}>
              <Text style={s.sectionTitle}>Cliente</Text>
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Nome:</Text>
                <Text style={s.infoValueBold}>{orcamento.cliente?.nome}</Text>
              </View>
              {orcamento.cliente?.telefone && (
                <View style={s.infoRow}>
                  <Text style={s.infoLabel}>Telefone:</Text>
                  <Text style={s.infoValue}>{orcamento.cliente.telefone}</Text>
                </View>
              )}
              {orcamento.cliente?.email && (
                <View style={s.infoRow}>
                  <Text style={s.infoLabel}>E-mail:</Text>
                  <Text style={s.infoValue}>{orcamento.cliente.email}</Text>
                </View>
              )}
              {orcamento.cliente?.logradouro && (
                <View style={s.infoRow}>
                  <Text style={s.infoLabel}>Endereco:</Text>
                  <Text style={s.infoValue}>
                    {orcamento.cliente.logradouro}, {orcamento.cliente.numero}
                    {orcamento.cliente.complemento ? ` - ${orcamento.cliente.complemento}` : ''}{'\n'}
                    {orcamento.cliente.bairro} - {orcamento.cliente.cidade}/{orcamento.cliente.estado}
                    {orcamento.cliente.cep ? `  CEP ${orcamento.cliente.cep}` : ''}
                  </Text>
                </View>
              )}
            </View>

            <View style={s.infoColR}>
              <Text style={s.sectionTitle}>Dados do Orcamento</Text>
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Numero:</Text>
                <Text style={s.infoValueBold}>{orcamento.numero}</Text>
              </View>
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Emissao:</Text>
                <Text style={s.infoValue}>{new Date(orcamento.criadoEm).toLocaleDateString('pt-BR')}</Text>
              </View>
              {validadeStr && (
                <View style={s.infoRow}>
                  <Text style={s.infoLabel}>Validade:</Text>
                  <Text style={s.infoValue}>{validadeStr}</Text>
                </View>
              )}
              {orcamento.dataVisita && (
                <View style={s.infoRow}>
                  <Text style={s.infoLabel}>Visita:</Text>
                  <Text style={s.infoValue}>{new Date(orcamento.dataVisita).toLocaleString('pt-BR')}</Text>
                </View>
              )}
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Responsavel:</Text>
                <Text style={s.infoValue}>{orcamento.responsavel?.nome}</Text>
              </View>
              <View style={s.infoRow}>
                <Text style={s.infoLabel}>Total itens:</Text>
                <Text style={s.infoValue}>{orcamento.itens?.length ?? 0} janela(s)</Text>
              </View>
            </View>
          </View>

          <View style={s.divider} />

          {/* ── JANELAS / ITENS ── */}
          <Text style={s.sectionTitle}>Janelas / Ambientes</Text>

          {orcamento.itens?.map((item: any, idx: number) => {
            const m2 = area(item.larguraCm, item.alturaCm);
            return (
              <View key={item.id} style={s.itemCard} wrap={false}>

                {/* Card header */}
                <View style={s.itemHeader}>
                  <View style={s.itemHeaderLeft}>
                    <Text style={s.itemBadge}>{idx + 1}</Text>
                    <View>
                      <Text style={s.itemAmbiente}>{item.ambiente}</Text>
                      {item.descricaoJanela && (
                        <Text style={s.itemDescricao}>{item.descricaoJanela}</Text>
                      )}
                    </View>
                  </View>
                  <View style={s.itemSubtotalBlock}>
                    <Text style={s.itemSubtotalLabel}>Subtotal</Text>
                    <Text style={s.itemSubtotalValue}>R$ {fmt(item.subtotal)}</Text>
                  </View>
                </View>

                <View style={s.itemBody}>

                  {/* Medidas */}
                  <View style={s.medidasRow}>
                    {[
                      ...(!isCliente ? [
                        { label: 'Largura', val: `${item.larguraCm} cm` },
                        { label: 'Altura', val: `${item.alturaCm} cm` },
                        { label: 'Area', val: `${m2.toFixed(2)} m2` },
                      ] : []),
                      { label: 'Qtd', val: `${item.quantidade}x` },
                      { label: 'Instalacao', val: INSTALACAO_LABELS[item.tipoInstalacao] ?? item.tipoInstalacao },
                      { label: 'Acionamento', val: ACIONAMENTO_LABELS[item.acionamento] ?? item.acionamento },
                      { label: 'Bando', val: item.bando ? 'Sim' : 'Nao' },
                      ...(item.ladoComando ? [{ label: 'Lado comando', val: LADO_COMANDO_LABELS[item.ladoComando] ?? item.ladoComando }] : []),
                    ].map((m, i, arr) => (
                      <View key={m.label} style={i === arr.length - 1 ? s.medidaBoxLast : s.medidaBox}>
                        <Text style={s.medidaLabel}>{m.label}</Text>
                        <Text style={s.medidaValue}>{m.val}</Text>
                      </View>
                    ))}
                  </View>

                  {/* Produto */}
                  {item.produto && (
                    <View style={s.produtoBox}>
                      <Text style={s.produtoNome}>
                        {item.produto.tipoPersiana?.nome}  -  {item.produto.marca} {item.produto.modelo}
                      </Text>
                      <View style={s.produtoGrid}>
                        {[
                          { label: 'Material:', val: item.produto.material },
                          { label: 'Cor:', val: item.produto.cor },
                          { label: 'Garantia:', val: `${item.produto.garantiaMeses} meses` },
                          { label: 'Prazo:', val: `${item.produto.prazoEntregaDias} dias uteis` },
                        ].map(p => (
                          <View key={p.label} style={s.produtoItem}>
                            <Text style={s.infoLabel}>{p.label}</Text>
                            <Text style={s.infoValue}>{p.val}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}

                  {/* Precos */}
                  {!isCliente ? (
                    <View style={s.precosRow}>
                      <View style={s.precoBox}>
                        <Text style={s.precoLabel}>Preco/m2</Text>
                        <Text style={s.precoValue}>
                          R$ {fmt(m2 > 0 ? item.precoUnitario / m2 : 0)}
                        </Text>
                      </View>
                      <View style={s.precoBox}>
                        <Text style={s.precoLabel}>Instalacao</Text>
                        <Text style={s.precoValue}>R$ {fmt(item.precoInstalacao)}</Text>
                      </View>
                      <View style={s.precoBox}>
                        <Text style={s.precoLabel}>Unit. c/ inst.</Text>
                        <Text style={s.precoValue}>R$ {fmt(item.precoUnitario + item.precoInstalacao)}</Text>
                      </View>
                      {item.quantidade > 1 && (
                        <View style={s.precoBox}>
                          <Text style={s.precoLabel}>Subtotal</Text>
                          <Text style={{ ...s.precoValue, fontFamily: 'Helvetica-Bold', color: GREEN }}>
                            R$ {fmt(item.subtotal)}
                          </Text>
                        </View>
                      )}
                    </View>
                  ) : (
                    <View style={[s.precosRow, { justifyContent: 'flex-start' }]}>
                      <View style={s.precoBox}>
                        <Text style={s.precoLabel}>Quantidade</Text>
                        <Text style={s.precoValue}>{item.quantidade}x</Text>
                      </View>
                      <View style={[s.precoBox, { marginLeft: 18 }]}>
                        <Text style={s.precoLabel}>Subtotal</Text>
                        <Text style={{ ...s.precoValue, fontFamily: 'Helvetica-Bold', color: GREEN }}>
                          R$ {fmt(item.subtotal)}
                        </Text>
                      </View>
                    </View>
                  )}

                  {/* Fotos */}
                  {item.fotos?.length > 0 && (
                    <View style={s.fotosSection}>
                      <Text style={s.fotosSectionLabel}>
                        Fotos do local  ({item.fotos.length})
                      </Text>
                      <View style={s.fotosRow}>
                        {item.fotos.map((foto: any) => (
                          <Image
                            key={foto.id}
                            src={`${apiBase}${foto.url}`}
                            style={s.fotoImg}
                          />
                        ))}
                      </View>
                    </View>
                  )}

                  {/* Observacoes do item */}
                  {item.observacoes && (
                    <View style={s.obsItem}>
                      <Text style={s.obsText}>Obs: {item.observacoes}</Text>
                    </View>
                  )}
                </View>
              </View>
            );
          })}

          {/* ── RESUMO FINANCEIRO ── */}
          {orcamento.totalBruto > 0 && (
            <View style={s.summarySection}>
              <Text style={s.sectionTitle}>Resumo Financeiro</Text>
              <View style={s.summaryCard}>
                <View style={s.summaryRow}>
                  <Text style={s.summaryLabel}>
                    Subtotal  ({orcamento.itens?.length} item(ns))
                  </Text>
                  <Text style={s.summaryValue}>R$ {fmt(orcamento.totalBruto)}</Text>
                </View>
                {orcamento.descontoPercent > 0 && (
                  <View style={s.summaryRow}>
                    <Text style={s.summaryLabel}>Desconto  ({orcamento.descontoPercent}%)</Text>
                    <Text style={s.summaryDiscount}>
                      - R$ {fmt((orcamento.totalBruto * orcamento.descontoPercent) / 100)}
                    </Text>
                  </View>
                )}
                <View style={s.summaryLine} />
                <View style={s.summaryTotalRow}>
                  <Text style={s.summaryTotalLabel}>Total</Text>
                  <Text style={s.summaryTotalValue}>R$ {fmt(orcamento.totalLiquido)}</Text>
                </View>
              </View>
            </View>
          )}

          {/* ── OBSERVACOES GERAIS ── */}
          {orcamento.observacoesGerais && (
            <View style={s.notesSection}>
              <Text style={s.sectionTitle}>Observacoes Gerais</Text>
              <View style={s.notesBox}>
                <Text style={s.notesText}>{orcamento.observacoesGerais}</Text>
              </View>
            </View>
          )}

          {/* ── FECHAMENTO ── */}
          <View style={s.closingSection}>
            <Text style={s.closingText}>
              Agradecemos a preferencia!
              {validadeStr
                ? `  Este orcamento e valido ate ${validadeStr}.`
                : '  Este orcamento tem validade de 30 dias a partir da emissao.'}
              {'\n'}
              Em caso de duvidas, entre em contato conosco.
            </Text>
          </View>

        </View>

        {/* ── FOOTER (fixo em todas as paginas) ── */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>
            Palmeira Persianas  -  Gerado em {new Date().toLocaleDateString('pt-BR')}
          </Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) => `Pagina ${pageNumber} de ${totalPages}`}
          />
        </View>

      </Page>
    </Document>
  );
}
